import {
  WORKFLOW_FORM_FIELD_INPUT_TYPES,
  WorkflowFormFieldInputFiles,
  WorkflowFormSubmission,
  WorkflowNodeFormWithPopulatedFieldsExternal,
} from '@workflows/types';
import {
  getWorkflowControlFiles,
  getWorkflowFileConstraints,
  getWorkflowFileOrigin,
  type WorkflowFileFieldChange,
} from '@core/services/workflow';

type SubmissionData = NonNullable<WorkflowFormSubmission['data']>;
type SubmissionFieldValue = SubmissionData[string];

type BuildChangedSubmissionDataParams = {
  schema: WorkflowNodeFormWithPopulatedFieldsExternal['schema'];
  formValue: Record<string, unknown>;
  existingSubmissionData?: WorkflowFormSubmission['data'];
};

type BuildChangedSubmissionDataResult = {
  changedData: SubmissionData;
  invalidNumberFieldKey?: string;
};

type BuildFileFieldsDiffParams = {
  schema: WorkflowNodeFormWithPopulatedFieldsExternal['schema'];
  formValue: Record<string, unknown>;
  /** Файлы, которыми поля были заполнены при загрузке формы (сервер + очередь) */
  loadedFiles: Readonly<Record<string, readonly File[]>>;
};

type NormalizeFieldValueResult = {
  isValid: boolean;
  isEmpty: boolean;
  value?: unknown;
};

const INPUT_FIELD_TYPES = new Set<string>(
  Object.values(WORKFLOW_FORM_FIELD_INPUT_TYPES) as string[],
);

/** Файлы и фото синхронизируются отдельными запросами, не через data формы */
const FILE_FIELD_TYPES = new Set<string>([
  WORKFLOW_FORM_FIELD_INPUT_TYPES.inputFiles,
  WORKFLOW_FORM_FIELD_INPUT_TYPES.inputPhotos,
]);

/**
 * Изменения файлов полей inputFiles: новые файлы (ещё не на сервере и не в очереди)
 * загружаются, загруженные ранее и убранные из поля — удаляются.
 * Удаляются только файлы, которые были показаны пользователю в поле.
 */
export function buildFileFieldsDiff(
  params: BuildFileFieldsDiffParams,
): WorkflowFileFieldChange[] {
  const { schema, formValue, loadedFiles } = params;
  const changes: WorkflowFileFieldChange[] = [];

  for (const group of schema.groups) {
    for (const field of group.fields) {
      if (field.type !== WORKFLOW_FORM_FIELD_INPUT_TYPES.inputFiles) {
        continue;
      }

      const fileField = field as WorkflowFormFieldInputFiles;
      const currentFiles = getWorkflowControlFiles(formValue[fileField.key]);
      const loaded = loadedFiles[fileField.key] ?? [];
      const upload = currentFiles.filter(file => !getWorkflowFileOrigin(file));
      const remove = loaded.filter(file => !currentFiles.includes(file));

      if (upload.length === 0 && remove.length === 0) {
        continue;
      }

      changes.push({
        fieldKey: fileField.key,
        upload,
        remove,
        existingCount: loaded.length,
        maxFilesCount: getWorkflowFileConstraints(fileField).maxFilesCount,
      });
    }
  }

  return changes;
}

/**
 * Строит payload только с изменёнными полями формы.
 * Одновременно валидирует inputNumber. Поля файлов и фото не входят в data (см. buildFileFieldsDiff).
 */
export function buildChangedSubmissionData(
  params: BuildChangedSubmissionDataParams,
): BuildChangedSubmissionDataResult {
  const { schema, formValue, existingSubmissionData } = params;
  const changedData: SubmissionData = {};
  let invalidNumberFieldKey: string | undefined;

  for (const group of schema.groups) {
    for (const field of group.fields) {
      if (
        !INPUT_FIELD_TYPES.has(field.type) ||
        FILE_FIELD_TYPES.has(field.type)
      ) {
        continue;
      }

      const fieldKey = field.key;
      const fieldType = field.type;
      const normalized = normalizeFieldValueForSync(
        formValue[fieldKey],
        fieldType,
      );

      if (!normalized.isValid) {
        invalidNumberFieldKey = invalidNumberFieldKey || fieldKey;
        continue;
      }

      if (normalized.isEmpty) {
        // Поле было заполнено, а теперь пустое — явная очистка (backend удаляет значение)
        if (
          !isEmptyComparableValue(
            normalizeValueForCompare(
              getExistingFieldValue(existingSubmissionData?.[fieldKey]),
              fieldType,
            ),
          )
        ) {
          changedData[fieldKey] = { value: null };
        }

        continue;
      }

      const normalizedValue = normalized.value;
      const existingComparableValue = normalizeValueForCompare(
        getExistingFieldValue(existingSubmissionData?.[fieldKey]),
        fieldType,
      );
      const hasChanged = !areComparableValuesEqual(
        normalizedValue,
        existingComparableValue,
      );

      if (hasChanged) {
        changedData[fieldKey] =
          serializeFieldValueForSubmission(normalizedValue);
      }
    }
  }

  return {
    changedData,
    invalidNumberFieldKey,
  };
}

/**
 * Нормализует значение перед сравнением/отправкой.
 * Для строк делает trim, для inputNumber приводит к числу.
 */
function normalizeFieldValueForSync(
  value: unknown,
  fieldType: string,
): NormalizeFieldValueResult {
  if (value === null || value === undefined) {
    return { isValid: true, isEmpty: true };
  }

  if (fieldType === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputNumber) {
    const normalizedNumber = normalizeInputNumberValue(value);

    if (normalizedNumber === undefined) {
      return { isValid: false, isEmpty: false };
    }

    return {
      isValid: true,
      isEmpty: normalizedNumber === null,
      value: normalizedNumber,
    };
  }

  const trimmedValue = trimStringValues(value);

  return {
    isValid: true,
    isEmpty: isEmptyComparableValue(trimmedValue),
    value: trimmedValue,
  };
}

/**
 * Пустое значение поля: null/undefined, пустая строка, пустой массив
 * или индексированный массив без элементов
 */
function isEmptyComparableValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (isPlainObject(value) && Object.keys(value).length === 0) {
    return true;
  }

  return false;
}

/**
 * Приводит значение числового поля к number.
 * Возвращает undefined, если значение невозможно интерпретировать как число.
 */
function normalizeInputNumberValue(value: unknown): number | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const parsedValue = Number(trimmed.replace(',', '.'));

  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

/**
 * Сохранённое значение поля: `{ value }` или индексированный массив
 * `{ "0": { value }, ... }` (multiselect), который сравнивается целиком
 */
function getExistingFieldValue(entry: unknown): unknown {
  if (isIndexedSubmissionArrayObject(entry)) {
    return entry;
  }

  return isPlainObject(entry) ? entry['value'] : undefined;
}

/**
 * Нормализует значение из existing submission для корректного сравнения.
 */
function normalizeValueForCompare(value: unknown, fieldType: string): unknown {
  if (fieldType === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputNumber) {
    const normalizedNumber = normalizeInputNumberValue(value);

    return normalizedNumber === undefined ? value : normalizedNumber;
  }

  return trimStringValues(value);
}

/**
 * Приводит значение к формату backend: { value }.
 * Для массивов сохраняет совместимость со старым indexed-форматом.
 */
function serializeFieldValueForSubmission(
  value: unknown,
): SubmissionFieldValue {
  if (Array.isArray(value)) {
    const indexedValue = value.reduce<Record<string, { value: unknown }>>(
      (acc, item, index) => {
        acc[String(index)] = { value: item };

        return acc;
      },
      {},
    );

    return indexedValue as SubmissionFieldValue;
  }

  return { value };
}

/**
 * Сравнивает два значения в "каноническом" представлении.
 */
function areComparableValuesEqual(left: unknown, right: unknown): boolean {
  const leftComparable = buildComparableValue(left);
  const rightComparable = buildComparableValue(right);

  return areValuesDeepEqual(leftComparable, rightComparable);
}

/**
 * Преобразует сложные значения к стабильному виду для deep-equal:
 * - Date -> ISO string
 * - File -> meta-объект
 * - indexed-object -> массив в фиксированном порядке
 * - object -> ключи в сортированном порядке
 */
function buildComparableValue(value: unknown): unknown {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof File) {
    return {
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified,
    };
  }

  if (Array.isArray(value)) {
    return value.map(item => buildComparableValue(item));
  }

  if (isIndexedSubmissionArrayObject(value)) {
    return Object.keys(value)
      .sort((a, b) => Number(a) - Number(b))
      .map(key => {
        const item = value[key] as { value?: unknown };

        return buildComparableValue(item?.value);
      });
  }

  if (isPlainObject(value)) {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = buildComparableValue(value[key]);

        return acc;
      }, {});
  }

  return value;
}

/**
 * Рекурсивное сравнение массивов и plain-object.
 */
function areValuesDeepEqual(left: unknown, right: unknown): boolean {
  if (left === right) {
    return true;
  }

  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((item, index) => areValuesDeepEqual(item, right[index]));
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    if (leftKeys.length !== rightKeys.length) {
      return false;
    }

    return leftKeys.every(key => areValuesDeepEqual(left[key], right[key]));
  }

  return false;
}

/**
 * Рекурсивно тримит все строковые значения.
 */
function trimStringValues(value: unknown): unknown {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map(item => trimStringValues(item));
  }

  if (isPlainObject(value)) {
    return Object.entries(value).reduce<Record<string, unknown>>(
      (acc, [key, item]) => {
        acc[key] = trimStringValues(item);

        return acc;
      },
      {},
    );
  }

  return value;
}

/**
 * Проверяет, что объект соответствует формату сериализованного массива:
 * { "0": { value: ... }, "1": { value: ... }, ... }
 */
function isIndexedSubmissionArrayObject(
  value: unknown,
): value is Record<string, unknown> {
  if (!isPlainObject(value)) {
    return false;
  }

  const keys = Object.keys(value);

  if (keys.length === 0) {
    return false;
  }

  return keys.every(key => {
    const item = value[key];

    return /^\d+$/.test(key) && isPlainObject(item) && 'value' in item;
  });
}

/**
 * Узкий type guard для plain-object (без массивов, Date и File).
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    !(value instanceof File)
  );
}
